import React, { useContext, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import './ProductsSyncMenu.sass'
import Button from '../../ui/Button/Button'
import DropdownBox from '../../ui/DropdownBox/DropdownBox'
import Icon from '../../ui/Icon/Icon'
import { usePopUp } from '../../hooks/usePopUp'
import { formatDateAsDayMontYearHHMM } from '../../utils/date/dateFormat'
import { normalizeSyncDate } from './syncDateUtils'
import { getProductsSyncContext, resolveSyncItems } from './productsSyncConfig'
import { useProductsSync } from './useProductsSync'
import XLSFeedPopUp from './XLSFeedPopUp/XLSFeedPopUp'
import { AuthContext } from '../../App'
import { isCatalogSyncAllowedUser } from '../../config/catalogSyncAccessControl'

function ProductsSyncMenu() {
  const location = useLocation()
  const { user } = useContext(AuthContext)
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const [isOpen, setIsOpen] = usePopUp(buttonRef, dropdownRef, true, false)
  const [xlsFeedPopUp, setXlsFeedPopUp] = useState(null)
  const { syncDates, loadingId, itemResults, runSync } = useProductsSync()
  const isSyncing = Boolean(loadingId)

  const syncContext = useMemo(
    () => ({
      ...getProductsSyncContext(location, { uid: user?.uid }),
      canUpdateSiteSearch: isCatalogSyncAllowedUser(user?.uid),
    }),
    [location, user?.uid]
  )
  const syncItems = useMemo(() => resolveSyncItems(syncContext), [syncContext])

  const handleItemClick = (item) => {
    if (item.disabled || isSyncing) {
      return
    }

    if (item.isFeedAction) {
      setIsOpen(false)
      setXlsFeedPopUp({
        variant: item.feedVariant,
        brandId: item.brandId,
      })
      return
    }

    if (item.isPlaceholder || !item.name) {
      return
    }

    runSync(item)
  }

  const getItemMetaContent = (item, isLoading) => {
    const result = itemResults[item.id]
    const lastSyncDate = normalizeSyncDate(item.dateKey ? syncDates[item.dateKey] : null)

    if (isLoading) {
      return <Icon name="sync" className="ProductsSyncMenu-ItemSpinner" size={12} />
    }

    if (item.isFeedAction) {
      return <span className="ProductsSyncMenu-ItemDate">—</span>
    }

    if (result?.ok === false) {
      const errorLabel =
        result.message && result.message.length <= 42 ? result.message : 'Помилка'

      return (
        <span className="ProductsSyncMenu-ItemError" title={result.message}>
          {errorLabel}
        </span>
      )
    }

    if (lastSyncDate) {
      const dateLabel = formatDateAsDayMontYearHHMM(lastSyncDate)
      return (
        <span className="ProductsSyncMenu-ItemDate" title={result?.summary || undefined}>
          {dateLabel}
        </span>
      )
    }

    if (result?.ok && result.summary) {
      return <span className="ProductsSyncMenu-ItemDate">{result.summary}</span>
    }

    return <span className="ProductsSyncMenu-ItemDate">—</span>
  }

  return (
    <>
      <div className="TabBut ProductsSyncMenu">
        <Button
          ref={buttonRef}
          type="button"
          theme="solid"
          fill="accent"
          size={28}
          icon="sync"
          iconPosition="left"
          className={`ProductsSyncMenu-Button${isSyncing ? ' ProductsSyncMenu-Button_syncing' : ''}`}
        >
          <span className="ProductsSyncMenu-ButtonInner">
            Синхронізація
            <Icon
              name={isOpen ? 'angle-up' : 'angle-down'}
              className="ProductsSyncMenu-ButtonChevron"
              size={10}
            />
          </span>
        </Button>
        {isOpen && (
          <DropdownBox
            ref={dropdownRef}
            className="ProductsSyncMenu-Dropdown"
            styles={{ top: '36px', right: 0, minWidth: '300px' }}
          >
            <ul className="ProductsSyncMenu-List">
              {syncItems.map((item) => {
                const isLoading = loadingId === item.id
                const itemClasses = [
                  'DropdownBox-Element',
                  'ProductsSyncMenu-Item',
                  item.disabled && 'ProductsSyncMenu-Item_disabled',
                ]
                  .filter(Boolean)
                  .join(' ')

                return (
                  <li
                    key={item.id}
                    className={itemClasses}
                    title={
                      item.disabled
                        ? item.disabledTitle
                        : itemResults[item.id]?.message || itemResults[item.id]?.summary
                    }
                    onClick={() => handleItemClick(item)}
                  >
                    <span className="ProductsSyncMenu-ItemLabel">{item.label}</span>
                    <span className="ProductsSyncMenu-ItemMeta">{getItemMetaContent(item, isLoading)}</span>
                  </li>
                )
              })}
            </ul>
          </DropdownBox>
        )}
      </div>
      <XLSFeedPopUp
        show={Boolean(xlsFeedPopUp)}
        variant={xlsFeedPopUp?.variant}
        brandId={xlsFeedPopUp?.brandId}
        onClose={() => setXlsFeedPopUp(null)}
      />
    </>
  )
}

export default ProductsSyncMenu
